import { useMemo } from "react"

import type { MetricMetadata } from "@/types/metrics/metadata"

import { type MetricSemanticType } from "../types"
import {
  getMetricType,
  METRIC_TYPE,
  type MetricTypeSource,
} from "../utils/resolveThreshold"

interface MetricTypeInfo {
  metricType: MetricSemanticType
  /** Options for categorical metrics; empty for non-categorical. */
  categoryOptions: string[]
}

type MetricTypeInfoSource = MetricTypeSource & {
  getCategoricalOptions?: () => string[]
  metric_metadata?: Pick<MetricMetadata, "additional_information"> | null
}

/**
 * Determine the semantic type of a metric and the valid value options for
 * type-aware condition / threshold inputs.
 *
 * Detection uses the metric-display semantic helpers so configure does not
 * need to know about runtime record subclasses.
 *
 * Category options come from the hydrated MetricRecord's `getCategoricalOptions`
 * method (overridden by SentimentAnalysisMetricRecord, EndReasonMetricRecord,
 * AudioSentimentMetricRecord, and MatchExpectedSimWrapperMetricRecord for
 * built-in category lists), falling back to the comma-separated
 * `metric_metadata.additional_information.value` for custom categorical metrics.
 */
export function getMetricTypeInfo(
  metric: MetricTypeInfoSource | undefined,
): MetricTypeInfo {
  if (!metric) {
    return { metricType: "numerical", categoryOptions: [] }
  }

  switch (getMetricType(metric)) {
    case METRIC_TYPE.BINARY:
      return { metricType: "binary", categoryOptions: [] }
    case METRIC_TYPE.CATEGORICAL:
      return {
        metricType: "categorical",
        categoryOptions: extractCategoryOptions(metric),
      }
    case METRIC_TYPE.NUMERICAL:
      return { metricType: "numerical", categoryOptions: [] }
    case METRIC_TYPE.SET:
    case METRIC_TYPE.UNTYPED:
      return { metricType: "untyped", categoryOptions: [] }
  }
}

/**
 * Hook wrapper around `getMetricTypeInfo` that memoizes the result.
 */
export function useMetricTypeInfo(
  metric: MetricTypeInfoSource | undefined,
): MetricTypeInfo {
  return useMemo(() => getMetricTypeInfo(metric), [metric])
}

function extractCategoryOptions(metric: MetricTypeInfoSource): string[] {
  if (typeof metric.getCategoricalOptions === "function") {
    return metric.getCategoricalOptions()
  }

  const raw = metric.metric_metadata?.additional_information?.value
  if (typeof raw !== "string") {
    return []
  }
  return raw
    .split(",")
    .map((v) => v.trim())
    .filter((v) => v.length > 0)
}
